import { DefaultValues } from "./../../types/common/modeling/modeling-types";
import produce from "immer";

import {
    REPORT_MODELING_DELTA_STATUSES,
    addItemToRoleTree,
    modifyItemFieldInRoleTree,
    updateRoleTreeOrgLevels,
    removeItemFromRoleTree,
    substractPfcgTreeFromRole,
} from "../../utils/reports-modeling-utils";

import { addRoleMenuFolder, addRoleMenuNode, openRoleMenuNode, removeRoleMenuNode, copyRoleMenu } from "../../utils/role-menu";

import { addGroupRoleChildren, removeGroupRoleChildren } from "../../utils/group-role-utils";
import { ModelingRole, Transaction } from "../../types/user-modeling/user-modeling-types";
import { PayloadAction } from "@reduxjs/toolkit";
import { RoleMenuNode, TreeValue } from "../../types/common/modeling/modeling-types";

const getSetModelingRoleTreeFieldChanges = (role: ModelingRole, action: PayloadAction<{ itemPath: string[]; field: string; value: TreeValue | null | boolean }>) => {
    const { itemPath, field, value } = action.payload;

    const changes = {
        ...produce({ tree: role.tree, delta: role.delta }, ({ tree, delta }) => {
            modifyItemFieldInRoleTree(role.orgLevels, tree, itemPath, field, value, delta);
        }),
        ...(field !== "open" && {
            status: role.status || REPORT_MODELING_DELTA_STATUSES.CHANGED,
        }),
    };

    return changes;
};

const getAddModelingRoleTreeItemsChanges = (role: ModelingRole, action: PayloadAction<{ id: string; items: []; defaultValues: DefaultValues[]; isStandard: boolean }>) => {
    const { items, defaultValues = [], isStandard } = action.payload;

    const changes = {
        ...produce(
            {
                tree: role.tree,
                orgLevels: role.orgLevels,
                delta: role.delta,
                orgLevelsDelta: role.orgLevelsDelta,
            },
            ({ tree, orgLevels, delta, orgLevelsDelta }) => {
                for (const item of items) {
                    try {
                        addItemToRoleTree(role.profile, orgLevels, tree, item, null, 0, defaultValues, delta, orgLevelsDelta, {}, isStandard);
                    } catch (error) {
                        console.log(error);
                    }
                }
            }
        ),
        status: role.status || REPORT_MODELING_DELTA_STATUSES.CHANGED,
    };

    return changes;
};

const getRemoveModelingRoleTreeItemChanges = (role: ModelingRole, action: PayloadAction<{ path: string[] }>) => {
    const { path } = action.payload;

    const changes = produce(
        { tree: role.tree, delta: role.delta, orgLevels: role.orgLevels, orgLevelsDelta: role.orgLevelsDelta },
        ({ tree, delta, orgLevels, orgLevelsDelta }) => {
            removeItemFromRoleTree(tree, path, delta, orgLevels, orgLevelsDelta);
        }
    );

    changes["status"] = role.status || REPORT_MODELING_DELTA_STATUSES.CHANGED;

    return changes;
};

const getSetModelingRoleOrgLevelValuesChanges = (role: ModelingRole, action: PayloadAction<{ values: Record<string, TreeValue> }>) => {
    const { values } = action.payload;

    const changes = produce({ orgLevels: role.orgLevels, orgLevelsDelta: role.orgLevelsDelta, tree: role.tree }, ({ orgLevels, orgLevelsDelta, tree }) =>
        updateRoleTreeOrgLevels(orgLevels, values, orgLevelsDelta, tree)
    );

    changes["status"] = role.status || REPORT_MODELING_DELTA_STATUSES.CHANGED;

    return changes;
};

const getOpenRoleMenuNode = (role, action: PayloadAction<{ id: string; itemPath: string[] }>) => {
    const { itemPath } = action.payload;

    const changes = {
        menu: produce(role.menu, (draftMenu) => openRoleMenuNode(itemPath, draftMenu)),
    };

    return changes;
};

const getAddRoleMenuFolder = (role: ModelingRole, action: PayloadAction<{ activeNodePath: string[]; folderName: string }>) => {
    const { activeNodePath, folderName } = action.payload;

    const changes = {
        menu: produce(role.menu, (draftMenu) => addRoleMenuFolder(activeNodePath, draftMenu, folderName)),
        status: role.status || REPORT_MODELING_DELTA_STATUSES.CHANGED,
    };

    return changes;
};

const getAddRoleMenuNode = (role: ModelingRole, action) => {
    const { activeNodePath, tcodes, transactions } = action.payload;

    const changes = {
        menu: produce(role.menu, (draftMenu) => addRoleMenuNode(activeNodePath, draftMenu, tcodes, transactions)),
        status: role.status || REPORT_MODELING_DELTA_STATUSES.CHANGED,
    };

    return changes;
};

const getRemoveRoleMenuNode = (role: ModelingRole, action: PayloadAction<{ activeNodePath: string[] }>) => {
    const { activeNodePath } = action.payload;

    const changes = {
        menu: produce(role.menu, (draftMenu) => removeRoleMenuNode(activeNodePath, draftMenu)),
        status: role.status || REPORT_MODELING_DELTA_STATUSES.CHANGED,
    };

    return changes;
};

const getSubstractPfcgTreeFromRole = (
    role: ModelingRole,
    action: PayloadAction<{
        orgLevelsForDelete: Set<string>;
        profilesForDelete: {
            [objectName: string]: Set<{
                [field: number]: Set<{ value: { from: string; to: string } }>;
            }>;
        };
        removedTcodes?: string[];
    }>
) => {
    const { orgLevelsForDelete, profilesForDelete = {}, removedTcodes = [] } = action.payload;

    const changes = {
        ...produce(
            {
                tree: role.tree,
                delta: role.delta,
                orgLevels: role.orgLevels,
                orgLevelsDelta: role.orgLevelsDelta,
            },
            ({ tree, delta, orgLevels, orgLevelsDelta }) => substractPfcgTreeFromRole(tree, delta, orgLevels, orgLevelsDelta, orgLevelsForDelete, profilesForDelete, removedTcodes)
        ),
        status: role.status || REPORT_MODELING_DELTA_STATUSES.CHANGED,
    };

    changes.tree = changes.tree.filter((node) => node.children?.length);

    return changes;
};

const getCopyRoleMenu = (role, action) => {
    const { menuToCopy } = action.payload;

    const changes = {
        menu: produce(role.menu, (draftMenu) => copyRoleMenu(menuToCopy, draftMenu)),
        status: role.status || REPORT_MODELING_DELTA_STATUSES.CHANGED,
    };

    return changes;
};

const getAddGroupRoleChild = (role, action) => {
    const { newChildRole, newChildRoleId } = action.payload;

    const changes = {
        children: produce(role.children, (draftChildren) => addGroupRoleChildren(newChildRole, newChildRoleId, draftChildren)),
        status: role.status || REPORT_MODELING_DELTA_STATUSES.CHANGED,
    };

    return changes;
};

const getRemoveGroupRoleChild = (role, action) => {
    const { roleIdToRemove } = action.payload;

    const changes = {
        children: removeGroupRoleChildren(roleIdToRemove, role.children),
        status: role.status || REPORT_MODELING_DELTA_STATUSES.CHANGED,
    };

    return changes;
};

export const getSelectedRoleReducers = (rolesSelector, rolesAdapter, setPreviousRole, shouldSetChangesStatus) => {
    const changeRole = (state, action, getChanges) => {
        const { id } = action.payload;
        const roles = rolesSelector(state);
        const role = roles.entities[id];

        if (!role || !(role.tree || role.children)) {
            return;
        }

        const changes = getChanges(role, action);

        if (changes.status) {
            setPreviousRole(state, role);
        }

        if (!shouldSetChangesStatus) {
            delete changes.status;
        }

        rolesAdapter.updateOne(roles, {
            id,
            changes,
        });
    };

    return {
        setModelingRoleTreeField: (state, action: PayloadAction<{ id: string; itemPath: string[]; field: string; value: TreeValue | null | boolean }>) =>
            changeRole(state, action, getSetModelingRoleTreeFieldChanges),

        addModelingRoleTreeItems: (state, action: PayloadAction<{ id: string; items: []; defaultValues?: DefaultValues[]; tcodes?: string[]; isStandard?: boolean }>) =>
            changeRole(state, action, getAddModelingRoleTreeItemsChanges),

        removeModelingRoleTreeItem: (state, action: PayloadAction<{ id: string; path: string[] }>) => changeRole(state, action, getRemoveModelingRoleTreeItemChanges),

        setModelingRoleOrgLevelValues: (state, action: PayloadAction<{ id: string; values: Record<string, TreeValue> }>) =>
            changeRole(state, action, getSetModelingRoleOrgLevelValuesChanges),

        openRoleMenuNode: (state, action: PayloadAction<{ id: string; itemPath: string[] }>) => changeRole(state, action, getOpenRoleMenuNode),

        addRoleMenuFolder: (state, action: PayloadAction<{ id: string; activeNodePath: string[]; folderName: string }>) => changeRole(state, action, getAddRoleMenuFolder),

        addRoleMenuNode: (state, action: PayloadAction<{ id: string; activeNodePath: string[]; tcodes: string[]; transactions: Transaction[] }>) =>
            changeRole(state, action, getAddRoleMenuNode),

        removeRoleMenuNode: (state, action: PayloadAction<{ id: string; activeNodePath: string[] }>) => changeRole(state, action, getRemoveRoleMenuNode),

        copyRoleMenu: (state, action: PayloadAction<{ id: string; menuToCopy: RoleMenuNode[] }>) => changeRole(state, action, getCopyRoleMenu),

        addGroupRoleChild: (state, action) => changeRole(state, action, getAddGroupRoleChild),

        removeGroupRoleChild: (state, action) => changeRole(state, action, getRemoveGroupRoleChild),

        substractPfcgTreeFromRole: (
            state,
            action: PayloadAction<{
                id: string;
                orgLevelsForDelete: Set<string>;
                profilesForDelete: {
                    [objectName: string]: Set<{
                        [field: number]: Set<{ value: { from: string; to: string } }>;
                    }>;
                };
                removedTcodes?: string[];
            }>
        ) => changeRole(state, action, getSubstractPfcgTreeFromRole),
    };
};
